import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { HubConnectionState } from "@microsoft/signalr";
import { OperationalControlsDto } from "@/api/models";
import OperationalControlsApiService from "@/api/OperationalControlsApiService";
import { DeploymentRequestsHubConnection } from "@/services/DeploymentRequestsHubConnection";
import auth from "@/services/Auth";

const RETRY_DELAY_MS = 2000;

export const useOperationalControlsStore = defineStore(
  "operational-controls",
  () => {
    const controls = ref<OperationalControlsDto | null>(null);
    const isLoaded = ref(false);
    const isSignalRConnected = ref(false);
    const isInitializing = ref(false);

    let hubConnection: DeploymentRequestsHubConnection | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const disableDeploymentsTemporarily = computed(() => {
      return (
        controls.value?.actions?.disableDeploymentsTemporarily ?? false
      );
    });

    const disableCreateDeployment = computed(() => {
      return controls.value?.actions?.disableCreateDeployment ?? false;
    });

    const disableRunAutomationTests = computed(() => {
      return (
        (controls.value?.actions?.disableRunAutomationTests ?? false)
      );
    });

    const disabledActionLabels = computed<string[]>(() => {
      const labels: string[] = [];

      if (disableCreateDeployment.value) {
        labels.push("Creating new deployments (including resume)");
      }

      if (disableDeploymentsTemporarily.value) {
        labels.push(
          "Changing existing deployments (pause, resume, reset, or destroy, automation tests)",
        );
      }

      if (disableRunAutomationTests.value) {
        labels.push("Running automation tests");
      }

      return labels;
    });

    const hasDisabledActions = computed(() => {
      return disabledActionLabels.value.length > 0;
    });

    function onConnectionChanged(state: HubConnectionState): void {
      isSignalRConnected.value = state === HubConnectionState.Connected;
    }

    function onOperationalControlsUpdated(
      updatedControls: OperationalControlsDto,
    ): void {
      controls.value = updatedControls;
      isLoaded.value = true;
    }

    async function loadControls(): Promise<void> {
      try {
        const result = await OperationalControlsApiService.get();
        controls.value = result.data.result ?? null;
      } catch {
        controls.value = null;
      } finally {
        isLoaded.value = true;
      }
    }

    function clearRetryTimer(): void {
      if (retryTimer == null) {
        return;
      }
      clearTimeout(retryTimer);
      retryTimer = null;
    }

    function scheduleRetry(): void {
      clearRetryTimer();
      retryTimer = setTimeout(() => {
        initialize().catch(() => {
          // Ignore retry errors and continue retry loop.
        });
      }, RETRY_DELAY_MS);
    }

    async function initialize(): Promise<void> {
      if (isInitializing.value) {
        return;
      }

      clearRetryTimer();

      if (!auth.isAuthenticated) {
        scheduleRetry();
        return;
      }

      isInitializing.value = true;

      try {
        await loadControls();

        if (hubConnection) {
          return;
        }

        hubConnection = new DeploymentRequestsHubConnection();
        hubConnection.addConnectionChangedListener(onConnectionChanged);
        hubConnection.addOperationalControlsUpdatedListener(
          onOperationalControlsUpdated,
        );
        await hubConnection.init();
      } catch {
        isSignalRConnected.value = false;

        if (hubConnection) {
          hubConnection.removeOperationalControlsUpdatedListener(
            onOperationalControlsUpdated,
          );
          hubConnection.removeConnectionChangedListener(onConnectionChanged);
          hubConnection = null;
        }

        scheduleRetry();
      } finally {
        isInitializing.value = false;
      }
    }

    function cleanup(): void {
      clearRetryTimer();

      if (hubConnection) {
        hubConnection.removeOperationalControlsUpdatedListener(
          onOperationalControlsUpdated,
        );
        hubConnection.removeConnectionChangedListener(onConnectionChanged);
        hubConnection = null;
      }

      isSignalRConnected.value = false;
      isInitializing.value = false;
    }

    return {
      controls,
      isLoaded,
      isSignalRConnected,
      initialize,
      cleanup,
      disabledActionLabels,
      hasDisabledActions,
    };
  },
);
